/**
 * LazyMDEditor Component
 *
 * Lazy-loaded wrapper around @uiw/react-md-editor to reduce initial bundle size.
 * The editor library (~150KB+ minified) is only loaded when the component is rendered.
 *
 * Usage:
 *   import { LazyMDEditor, LazyMDEditorMarkdown, EditorSkeleton } from './components/LazyMDEditor'
 *
 *   // For editing
 *   <Suspense fallback={<EditorSkeleton />}>
 *     <LazyMDEditor value={content} onChange={setContent} />
 *   </Suspense>
 *
 *   // For preview only
 *   <Suspense fallback={<EditorSkeleton />}>
 *     <LazyMDEditorMarkdown source={content} />
 *   </Suspense>
 */

import { lazy, forwardRef, type ComponentProps } from 'react'
import { Loader2 } from 'lucide-react'
import type MDEditorType from '@uiw/react-md-editor'

// Lazy load the main MDEditor component
const MDEditor = lazy(() => import('@uiw/react-md-editor'))

// Lazy load MDEditor.Markdown separately since it's a static property
// We import the whole module and extract the Markdown component
const MDEditorMarkdown = lazy(() =>
  import('@uiw/react-md-editor').then((mod) => ({
    default: mod.default.Markdown,
  }))
)

// Extract prop types from the original MDEditor
type MDEditorProps = ComponentProps<typeof MDEditorType>
type MDEditorMarkdownProps = ComponentProps<typeof MDEditorType.Markdown>

/**
 * EditorSkeleton Component
 *
 * Loading skeleton that matches the editor's dimensions to prevent layout shift.
 * Used as a Suspense fallback while the MDEditor lazy loads.
 */
export function EditorSkeleton() {
  return (
    <div
      className="flex items-center justify-center bg-gray-50 border border-gray-200 rounded-md"
      style={{ minHeight: '500px', height: '100%' }}
    >
      <div className="flex flex-col items-center gap-3 text-gray-500">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        <span className="text-sm">Loading editor...</span>
      </div>
    </div>
  )
}

/**
 * Lazy-loaded MDEditor component for editing markdown content
 * Wraps the @uiw/react-md-editor main editor component
 */
export const LazyMDEditor = forwardRef<HTMLDivElement, MDEditorProps>(
  function LazyMDEditor(props, ref) {
    return <MDEditor ref={ref} {...props} />
  }
)

/**
 * Lazy-loaded MDEditor.Markdown component for rendering markdown content
 * Wraps the @uiw/react-md-editor preview/markdown renderer
 */
export const LazyMDEditorMarkdown = function LazyMDEditorMarkdown(props: MDEditorMarkdownProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return <MDEditorMarkdown {...(props as any)} />
}

// Re-export types for consumers
export type { MDEditorProps, MDEditorMarkdownProps }
